import React, { useContext, useState } from 'react';
import { Plus, X, Info } from 'lucide-react';
import * as Tooltip from '@radix-ui/react-tooltip';
import { Switch } from '../../../../components/ui/switch';
import Button from '../../../../components/Button/Button';
import CustomSelect from '../../../../components/CustomSelect/CustomSelect';
import SlotMapping from '../../../../types/BotBuilder/SlotMapping';
import BotBuilderContext from '../../../../context/BotBuilderContext/BotBuilderContext';
import IntentSlot from '../../../../types/BotBuilder/IntentSlot';
import SlotValueType from '../../../../types/BotBuilder/SlotValueType';
import GoToIntentSummary from '../../../../types/BotBuilder/GoToIntentSummary';
import EnumeratedValueInput from './EnumeratedValueInput';

interface SlotMappingConfigurationProps {
  slotMappings: SlotMapping[];
  onSlotMappingsChange: (mappings: SlotMapping[]) => void;
  targetIntent: GoToIntentSummary | null;
  disabled?: boolean;
}

function SlotMappingConfiguration({
  slotMappings,
  onSlotMappingsChange,
  targetIntent,
  disabled = false
}: Readonly<SlotMappingConfigurationProps>): React.ReactElement | null {
  const { slots } = useContext(BotBuilderContext);
  const [savedMappings, setSavedMappings] = useState<SlotMapping[]>([]);

  const targetIntentSlots = targetIntent?.slots || [];
  const targetIntentDetails = targetIntent;

  const isEnabled = slotMappings.length > 0;

  const toggleSlotMapping = (): void => {
    if (isEnabled) {
      setSavedMappings(slotMappings);
      onSlotMappingsChange([]);
      return;
    }

    if (savedMappings.length > 0) {
      onSlotMappingsChange(savedMappings);
    } else {
      const newMapping: SlotMapping = {
        responseCardValue: '',
        destinationSlotName: '',
        slotValue: ''
      };
      onSlotMappingsChange([newMapping]);
    }
  };

  const addSlotMapping = (): void => {
    const newMapping: SlotMapping = {
      responseCardValue: '',
      destinationSlotName: '',
      slotValue: ''
    };
    const updatedMappings = [...slotMappings, newMapping];
    onSlotMappingsChange(updatedMappings);
    setSavedMappings(updatedMappings);
  };

  const removeSlotMapping = (index: number): void => {
    const updatedMappings = slotMappings.filter((_, i) => i !== index);
    onSlotMappingsChange(updatedMappings);
    setSavedMappings(updatedMappings);
  };

  const updateSlotMapping = (
    index: number,
    destinationSlotName: string
  ): void => {
    const updatedMappings = slotMappings.map((mapping, i) =>
      i === index
        ? {
            ...mapping,
            destinationSlotName,
            responseCardValue: '',
            slotValue: ''
          }
        : mapping
    );
    onSlotMappingsChange(updatedMappings);
    setSavedMappings(updatedMappings);
  };

  const updateSlotValue = (index: number, value: string): void => {
    const updatedMappings = slotMappings.map((mapping, i) =>
      i === index ? { ...mapping, slotValue: value } : mapping
    );
    onSlotMappingsChange(updatedMappings);
    setSavedMappings(updatedMappings);
  };

  const renderInlineValueInput = (
    mapping: SlotMapping,
    index: number,
    intentSlot: IntentSlot
  ): React.ReactElement | null => {
    switch (intentSlot.inlineValueType) {
      case SlotValueType.FREE_TEXT:
        return (
          <input
            type='text'
            placeholder='Enter slot value'
            className='w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500'
            value={mapping.slotValue || ''}
            onChange={(e) => updateSlotValue(index, e.target.value)}
            disabled={disabled}
          />
        );

      case SlotValueType.NUMBER:
        return (
          <input
            type='number'
            placeholder='Enter slot value'
            className='w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500'
            value={mapping.slotValue || ''}
            onChange={(e) => updateSlotValue(index, e.target.value)}
            disabled={disabled}
          />
        );

      case SlotValueType.DATE:
        return (
          <input
            type='date'
            className='w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500'
            value={mapping.slotValue || ''}
            onChange={(e) => updateSlotValue(index, e.target.value)}
            disabled={disabled}
          />
        );

      case SlotValueType.ENUMERATED_VALUE:
        return (
          <CustomSelect
            value={mapping.slotValue || ''}
            onValueChange={(value) => updateSlotValue(index, value)}
            options={(intentSlot.inlineEnumeratedValues || []).map(
              (enumValue) => ({
                value: enumValue.value,
                label: enumValue.value
              })
            )}
            placeholder='Select slot value'
            className='bg-white'
            fullWidth
            disabled={disabled}
          />
        );

      default:
        return null;
    }
  };

  const renderValueInput = (
    mapping: SlotMapping,
    index: number
  ): React.ReactElement | null => {
    const selectedIntentSlot = targetIntentSlots.find(
      (intentSlot) => intentSlot.slotName === mapping.destinationSlotName
    );
    if (!selectedIntentSlot) return null;

    if (selectedIntentSlot.slotType === null) {
      if (!selectedIntentSlot.inlineValueType) return null;

      return renderInlineValueInput(mapping, index, selectedIntentSlot);
    }

    const selectedSlot = slots.find(
      (slot) => slot.slotName === selectedIntentSlot.slotType
    );
    if (!selectedSlot) return null;

    switch (selectedSlot.valueType) {
      case SlotValueType.FREE_TEXT:
        return (
          <input
            type='text'
            placeholder='Enter slot value'
            className='w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500'
            value={mapping.slotValue || ''}
            onChange={(e) => updateSlotValue(index, e.target.value)}
            disabled={disabled}
          />
        );

      case SlotValueType.NUMBER:
        return (
          <input
            type='number'
            placeholder='Enter slot value'
            className='w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500'
            value={mapping.slotValue || ''}
            onChange={(e) => updateSlotValue(index, e.target.value)}
            disabled={disabled}
          />
        );

      case SlotValueType.DATE:
        return (
          <input
            type='date'
            className='w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500'
            value={mapping.slotValue || ''}
            onChange={(e) => updateSlotValue(index, e.target.value)}
            disabled={disabled}
          />
        );

      case SlotValueType.ENUMERATED_VALUE:
        return (
          <EnumeratedValueInput
            selectedSlot={selectedSlot}
            mapping={mapping}
            index={index}
            targetIntentDetails={targetIntentDetails}
            updateSlotValue={updateSlotValue}
            disabled={disabled}
          />
        );

      default:
        return null;
    }
  };

  if (!targetIntent) {
    return null;
  }

  if (targetIntentSlots.length === 0) {
    return null;
  }

  return (
    <div>
      <div className='flex items-center mt-4 mb-4 ml-2'>
        <div className='flex items-center'>
          <Switch
            checked={isEnabled}
            onCheckedChange={toggleSlotMapping}
            id='slot-mapping-toggle'
            disabled={disabled}
          />
          <div className='flex items-center gap-2 ml-3'>
            <span className='text-sm'>Pre-populate variables</span>
            <Tooltip.Provider>
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <Info className='w-4 h-4 text-gray-500' />
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  <Tooltip.Content
                    className='bg-gray-900 text-white px-2 py-1 rounded text-xs max-w-xs'
                    side='right'
                  >
                    Automatically fill variable values in the destination
                    question
                    <Tooltip.Arrow className='fill-gray-900' />
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>
            </Tooltip.Provider>
          </div>
        </div>
      </div>

      {isEnabled && (
        <div>
          {slotMappings.map((mapping, index) => (
            <div
              key={`${mapping.destinationSlotName}-${mapping.slotValue}-${index}`}
              className='mb-6 border border-gray-200 rounded p-4'
            >
              <div className='flex justify-between items-center mb-4'>
                <h4 className='text-sm font-medium'>Variable {index + 1}</h4>
                <button
                  onClick={() => removeSlotMapping(index)}
                  className={`p-1 text-red-500 hover:text-red-700 rounded ${slotMappings.length > 1 ? 'visible' : 'invisible'}`}
                  type='button'
                  disabled={disabled}
                >
                  <X className='w-4 h-4' />
                </button>
              </div>

              <CustomSelect
                value={mapping.destinationSlotName}
                onValueChange={(value) => updateSlotMapping(index, value)}
                options={targetIntentSlots
                  .filter((intentSlot) => {
                    return (
                      intentSlot.slotName === mapping.destinationSlotName ||
                      !slotMappings.some(
                        (otherMapping, otherIndex) =>
                          otherIndex !== index &&
                          otherMapping.destinationSlotName ===
                            intentSlot.slotName
                      )
                    );
                  })
                  .map((intentSlot) => ({
                    value: intentSlot.slotName,
                    label: intentSlot.slotName
                  }))}
                placeholder='Select variable'
                className='bg-white'
                fullWidth
                disabled={disabled}
              />

              {renderValueInput(mapping, index)}
            </div>
          ))}

          {slotMappings.length < targetIntentSlots.length && (
            <div className='flex justify-end'>
              <Button
                icon={<Plus className='w-4 h-4' />}
                onClick={addSlotMapping}
                variant='outline'
                size='sm'
                disabled={disabled}
              >
                Add variable
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default SlotMappingConfiguration;
